import { createFieldStateReducer } from "@/components/form"
import { InputPasswordField } from "@/components/input-password-field"
import { useTranslatedFieldErrorMessage } from "@/hooks/use-translated-field-error-message"
import { trackAnalyticsEvent } from "@/lib/analytics"
import { legacyFilter } from "@/lib/validations/legacyFilter"
import { transformWrongCredentialsError } from "@/lib/validations/wrongCredentials"
import { ChangeEventHandler, FocusEventHandler, useEffect } from "react"
import {
  useController,
  UseControllerProps,
  useFormContext,
} from "react-hook-form"
import { LoginFormValues } from "./login-form"

type PasswordInputFieldProps = UseControllerProps<LoginFormValues> & {
  onBlur?: FocusEventHandler<HTMLInputElement>
  onChange?: ChangeEventHandler<HTMLInputElement>
}

export function LoginFormPasswordInput({
  control,
  onBlur,
  onChange,
}: PasswordInputFieldProps) {
  const { field, fieldState } = useController({ name: "password", control })
  const { setValue } = useFormContext<LoginFormValues>()

  const translatedErrorMessage = useTranslatedFieldErrorMessage(
    fieldState,
    passwordFieldStateReducer,
  )

  useEffect(() => {
    if (translatedErrorMessage) {
      trackAnalyticsEvent({
        event: "link_click",
        event_name: "form_error",
        conversion_pagename: "login",
        error_message: translatedErrorMessage,
        error_type: "password",
      })
    }
  }, [translatedErrorMessage])

  const handleBlur: FocusEventHandler<HTMLInputElement> = (event) => {
    field.onBlur()
    onBlur?.(event)
  }

  // workaround for legacy behaviour. WARNING: this replaces the user input
  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const inputValue = event.target.value
    const inputWithoutSpaces = inputValue.replace(/\s/g, "")
    const revisedInputValue = legacyFilter(inputWithoutSpaces)

    field.onChange(event)

    if (inputValue !== revisedInputValue) {
      setValue("password", revisedInputValue)
    }

    onChange?.(event)
  }

  const isSnai = window.APP_VENDOR === "snai"

  return (
    <InputPasswordField
      label="LoginPage.LoginForm.passwordFieldLabel"
      maxLength={isSnai ? 50 : 20}
      id="password"
      data-qa="login-form-password-input"
      {...field}
      fieldStateReducer={passwordFieldStateReducer}
      onBlur={handleBlur}
      onChange={handleChange}
    />
  )
}

export const PASSWORD_REQUIRED_ERROR = "Errors.passwordRequiredError"

const passwordFieldStateReducer = createFieldStateReducer({
  transformErrorMessage: transformWrongCredentialsError,
})
